import { createElement, type ReactNode } from 'react'
import * as R from 'ramda'
import type { Constant, Nil } from '@pbt/pbt-ui-components'
import { DateUtils, LanguageUtils, moment } from '@pbt/pbt-ui-components'

import { RxPrescriptionOrigin, RxType } from '~/api/graphql/generated/types'
import { getQtyRequested } from '~/components/dashboard/clients/timeline/prescriptions/rxTracker/getQtyRequested'
import {
  isChewyPharmacistDeclined,
  isChewyRxUsageCanceledOrder,
  type RxUsageStateLike,
} from '~/components/dashboard/clients/utils/orderStatusChipUtils'
import QuantityAndRefills from '~/components/dashboard/prescription/prescription-editor/QuantityAndRefills'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import i18n from '~/locales/i18n'
import type { PrescriptionTimelineItem, RxCardItem } from '~/types'
import type { Prescription } from '~/types/entities/prescriptions'
import {
  getPrescriptionType,
  getPrescriptionTypeLabel,
  getRxType,
} from '~/utils/prescription'
import { getRemainingRefillsCount } from '~/utils/prescriptionUtils'

import type {
  GetOrderTableRowOptions,
  OrderTableContent,
  OrderTableFieldRowOptions,
  OrderTableRow,
} from './orderTableTypes'

const toTrimmedString = (value: string | Nil): string =>
  value == null ? '' : String(value).trim()

export const EMPTY_FIELD_PLACEHOLDER = '--'

export const formatDeliveredDate = (
  dateStr: string | null | undefined,
): string => (dateStr ? DateUtils.formatDate(dateStr, false, 'MM/DD/YYYY') : '')

export const trackingDisplayFor = (
  tracking: string | null | undefined,
  carrier?: string | null,
): string => {
  const trackingNumber = tracking?.trim()
  if (!trackingNumber) return ''
  const carrierName =
    carrier?.trim() || i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FEDEX')
  return `${carrierName} # ${trackingNumber}`
}

/** Minimal shipment shape for shipping display. shipmentCarrier = carrier name (API field). */
export type ShipmentForDisplay = {
  deliveredAt?: string | null
  shipmentCarrier?: string | null
  trackingId?: string | null
}

/**
 * Resolves primary (date) and secondary (carrier + tracking) from shipments.
 * If deliveredAt: "Delivered on ...". Else if promiseDeliveryDate: "Arrives by ...". Else: "Preparing for shipment".
 */
export function getShippingDisplayFromShipments(
  shipments: ShipmentForDisplay[],
  promiseDeliveryDate?: string | null,
): { primary: string; secondary: string | null } {
  const list = [...(shipments ?? [])]
  const delivered = list
    .filter((s) => s.deliveredAt)
    .sort((a, b) => (b.deliveredAt ?? '').localeCompare(a.deliveredAt ?? ''))[0]
  if (delivered) {
    const primary = `${i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.DELIVERED_ON')} ${formatDeliveredDate(delivered.deliveredAt)}`
    const secondary = trackingDisplayFor(
      delivered.trackingId,
      delivered.shipmentCarrier,
    )
    return { primary, secondary: secondary || null }
  }

  if (promiseDeliveryDate) {
    const primary = `${i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ARRIVES_BY')} ${formatDeliveredDate(promiseDeliveryDate)}`
    const first = list[0]
    const secondary = first
      ? trackingDisplayFor(first.trackingId, first.shipmentCarrier)
      : null
    return { primary, secondary: secondary || null }
  }

  return {
    primary: i18n.t(
      'Clients:TIMELINE.RX_STATUS_TRACKER.PREPARING_FOR_SHIPMENT',
    ),
    secondary: null,
  }
}

/** Accepts rxUsages from PrescriptionV2 (GraphQL) or order table (RxUsageWithShipments[]). */
type HasRxUsagesValue =
  | { rxUsages?: Array<object | null> | null }
  | null
  | undefined

export const hasRxUsages = (value: HasRxUsagesValue): boolean =>
  Array.isArray(value?.rxUsages) && value.rxUsages.length > 0

export function hasOrderTableLoadedData(p: unknown): boolean {
  if (!p || typeof p !== 'object') return false
  const o = p as Record<string, unknown>
  return (Array.isArray(o.refills) && o.refills.length > 0) || hasRxUsages(o)
}

/** Shipment shape for Chewy filled check (deliveredAt or shippedAt = filled). */
type ChewyShipment = { deliveredAt?: string | null; shippedAt?: string | null }

/** Rx with rxUsages (e.g. from PrescriptionV2) for Chewy remaining/last-fill. */
type RxWithUsages = Prescription & {
  quantityNumbRefills?: number | null
  rxUsages?: Array<{
    rxOrder?: {
      placedAt?: string
      rxShipments?: Array<ChewyShipment> | null
    } | null
    rxUsageState?: RxUsageStateLike | null
  } | null> | null
}

/** Minimal shape for option; accepts loaded PrescriptionV2 (both properties optional so callers can pass without type errors). */
type RxForChewyRemaining = {
  quantityNumbRefills?: number | null
  rxUsages?: Array<{
    rxOrder?: {
      placedAt?: string
      rxShipments?: Array<ChewyShipment> | null
    } | null
  } | null> | null
}

/** True if a Chewy usage has shipped: at least one shipment with shippedAt. */
function isChewyUsageShipped(
  usage: {
    rxOrder?: { rxShipments?: Array<ChewyShipment> | null } | null
  } | null,
): boolean {
  const ships = usage?.rxOrder?.rxShipments ?? []
  return ships.some((s) => Boolean(s?.shippedAt?.trim()))
}

/** Shipped and not canceled — counts toward refills remaining only. */
function chewyUsageCountsTowardRefillsRemaining(
  usage: {
    rxOrder?: { rxShipments?: Array<ChewyShipment> | null } | null
    rxUsageState?: RxUsageStateLike | null
  } | null,
): boolean {
  return isChewyUsageShipped(usage) && !isChewyRxUsageCanceledOrder(usage)
}

/** Non-canceled usage with at least order date or shipment dates — eligible for last-fill display. */
function chewyNonCanceledUsageHasDateForLastFill(
  usage: {
    rxOrder?: {
      placedAt?: string
      rxShipments?: Array<ChewyShipment> | null
    } | null
  } | null,
): boolean {
  if (usage == null) return false
  const order = usage.rxOrder
  if (order?.placedAt?.trim()) return true
  return (order?.rxShipments ?? []).some((s) =>
    Boolean(s?.deliveredAt?.trim() || s?.shippedAt?.trim()),
  )
}

/**
 * Refills remaining for Chewy: refill slots left (excludes the original fill).
 * Counts shipped usages that are not order-canceled.
 */
export function getChewyRefillsRemaining(rx: RxWithUsages): number {
  if (!hasRxUsages(rx)) return 0
  const numRefillsAllowed = rx.quantityNumbRefills ?? 0
  const shippedCount = (rx.rxUsages ?? []).filter((u) =>
    chewyUsageCountsTowardRefillsRemaining(u),
  ).length
  const refillsUsed = Math.max(0, shippedCount - 1)
  return Math.max(numRefillsAllowed - refillsUsed, 0)
}

/**
 * Most recent date among non-canceled Chewy usages: order placedAt plus deliveredAt/shippedAt
 * from shipments. Pending (unshipped) orders contribute placedAt so last fill skips canceled
 * rows and reflects the latest active order (e.g. second card when the first is canceled).
 */
export function getChewyLastFillDate(rx: RxWithUsages): string | null {
  if (!hasRxUsages(rx)) return null
  const dates = (rx.rxUsages ?? [])
    .filter(
      (usage) =>
        usage != null &&
        !isChewyRxUsageCanceledOrder(usage) &&
        chewyNonCanceledUsageHasDateForLastFill(usage),
    )
    .flatMap((usage) => {
      const order = usage?.rxOrder
      const fromOrder = order?.placedAt ? [order.placedAt] : []
      const fromShips = (order?.rxShipments ?? []).flatMap((ship) => {
        const result: string[] = []
        const delivered = ship?.deliveredAt?.trim()
        const shipped = ship?.shippedAt?.trim()
        if (delivered) result.push(delivered)
        if (shipped) result.push(shipped)
        return result
      })
      return [...fromOrder, ...fromShips]
    })
  if (dates.length === 0) return null
  const sorted = [...dates].sort((a, b) => b.localeCompare(a))
  return sorted[0] ?? null
}

export const formatValueOrPlaceholder = (value: string | Nil): string => {
  const s = toTrimmedString(value)
  return s || EMPTY_FIELD_PLACEHOLDER
}

type NullableType<T> = T | Nil
type NullableString = NullableType<string>
type NullablePrescriptionType = NullableType<PrescriptionType>

export type DisplayField = {
  boldValue?: boolean
  label: string
  onClick?: () => void
  parentPrescription?: Prescription
  prescription?: Prescription
  relativeTime?: string | null
  secondaryText?: string | null
  tooltipText?: string
  /** When true, secondaryText uses date label styling. When false (default), uses regular text styling. */
  useSecondaryTextStyle?: boolean
  value: ReactNode | string
}

export interface PrescriptionFlags {
  isCanceled: boolean
  isChewy: boolean
  isDeclined: boolean
  isPendingVetApproval: boolean
  isScriptOut: boolean
  stateName: string | null
}

export interface GetRxFieldsOptions {
  DrugDeliveryMethod?: Constant[]
  DrugStrengthColor?: Constant[]
  DrugStrengthUnit?: Constant[]
  InventoryProductSizeUnit?: Constant[]
  ProductForm?: Constant[]
  isOrderedDrug?: boolean
  onDispenseRefill?: () => void
  /** When set (e.g. Chewy), used only for refills remaining and last fill so rxUsages are available; # of refills always uses main rx. */
  prescriptionForRefillsRemainingAndLastFill?: RxForChewyRemaining | null
  prescriptionProductSizeUnit?: Constant[]
  prescriptionStates?: Constant[]
  productSizeUnit?: Constant[]
  vetDietConstants?: import('~/components/dashboard/clients/timeline/prescriptions/rxTracker/getQtyRequested').VetDietConstants
}

export const getPrescriptionFlags = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  prescriptionStates?: Constant[],
): PrescriptionFlags => {
  const stateName =
    rx.stateId && prescriptionStates
      ? (prescriptionStates.find((s) => s.id === rx.stateId)?.name ?? null)
      : null

  const isDeclined = stateName === PrescriptionStates.DECLINED
  const isCanceled = stateName === PrescriptionStates.CANCELED
  const isPendingVetApproval =
    stateName === PrescriptionStates.PENDING_VET_APPROVAL

  return {
    stateName,
    isDeclined,
    isCanceled,
    isPendingVetApproval,
    isScriptOut: prescriptionType === PrescriptionType.SCRIPT_OUT,
    isChewy: prescriptionType === PrescriptionType.CHEWY,
  }
}

/**
 * True when the card should show the field set that includes Qty Requested (order date, fulfillment, qty requested).
 * Used for: declined, pending vet approval (and not canceled), and outside pharmacy (SCRIPT_OUT).
 */
export const shouldSuppressFields = (flags: PrescriptionFlags): boolean =>
  ((flags.isDeclined || flags.isPendingVetApproval) && !flags.isCanceled) ||
  flags.isScriptOut

const DateFormatters = {
  date: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY'),
  dateTime: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY | h:mmA'),
}

const formatDateOrPlaceholder = (
  value: NullableString,
  format: (v: NullableString) => string,
): string => {
  const s = toTrimmedString(value)
  return s ? format(value) : formatValueOrPlaceholder(value)
}

const formatDateValueOrPlaceholder = (
  value: NullableString,
  format: (v: string) => string,
): string => {
  const trimmed = toTrimmedString(value)
  if (!trimmed) return EMPTY_FIELD_PLACEHOLDER

  const formatted = format(trimmed)

  if (!formatted || /invalid\s*date/i.test(formatted)) {
    return EMPTY_FIELD_PLACEHOLDER
  }

  return formatted
}

export const computeRelativeTime = (
  date: NullableString,
  timezone: string,
): string | null => {
  if (!date) return null
  const m = moment.tz(date, timezone)
  return m.isValid() ? m.fromNow() : null
}

const isPrescriptionFilled = (rx: Prescription): boolean =>
  Boolean(rx.filledBy ?? rx.filledById)

export const getPrescriptionMostRecentDate = (
  rx: Prescription,
): string | null =>
  rx.modificationDate ??
  rx.updatedAt ??
  rx.creationDate ??
  (rx as { createdAt?: string }).createdAt ??
  null

const PrescriptionFields = {
  drugExpirationDate: (rx: Prescription): NullableString => {
    const flat = rx as {
      quantityDrugExpirationDate?: string | null
    }
    return flat.quantityDrugExpirationDate ?? null
  },
  refillExpirationDate: (rx: Prescription): NullableString => {
    const flat = rx as {
      quantityRefillExpirationDate?: string | null
    }
    return flat.quantityRefillExpirationDate ?? null
  },

  lastFillDate: (rx: Prescription, isInHouse = false): string | null => {
    const all = [rx, ...(rx.refills ?? [])]
    const dates = all
      .filter((p) => isInHouse || isPrescriptionFilled(p))
      .map(getPrescriptionMostRecentDate)
      .filter((date): date is string => date !== null)

    return dates.length > 0
      ? ([...dates].sort((a, b) => a.localeCompare(b)).pop() ?? null)
      : null
  },

  numberOfRefills: (rx: Prescription): number | null =>
    rx.quantityNumbRefills ?? null,

  refillsRemaining: (rx: Prescription): number => getRemainingRefillsCount(rx),
}

const toStringOrZero = (value: number | null | undefined): string =>
  value === null || value === undefined ? '0' : String(value)

/**
 * True when this **prescription object** is an inventory order (`type` or `prescriptionType === INVENTORY`).
 * Use at row/refill level when you do not have an {@link RxCardItem}.
 *
 * **Not** the same as {@link isDispensedFromInventory}: that is also true for in-house prescribe
 * (non-vet) and drives legacy “Dispensed from inventory” copy on {@link PrescriptionsCard} refills.
 */
export function isInventoryOrder(rx: Prescription): boolean {
  const r = rx as { prescriptionType?: string; type?: string }
  return (
    r.type === OrderType.INVENTORY || r.prescriptionType === OrderType.INVENTORY
  )
}

/**
 * True when the timeline **card** is an ordered/administered drug: `subType === drug` and the nested
 * prescription is an {@link isInventoryOrder}.
 *
 * For order-table **rows**, combine the card flag with {@link isInventoryOrder} via
 * {@link isOrderedDrugOrInventoryOrderRow} (same idea as the card, at row level).
 */
export function isOrderedDrugCard(item: RxCardItem): boolean {
  if (item.subType?.toLowerCase() !== 'drug') return false
  return item.prescription != null && isInventoryOrder(item.prescription)
}

/**
 * Rx tracker order table: row should use ordered-drug/inventory semantics (e.g. “Administered on …”)
 * when the **parent card** is an ordered drug or **this row’s** rx is an inventory order.
 *
 * Prefer this over {@link isDispensedFromInventory} for fill status and similar tracker copy.
 */
export function isOrderedDrugOrInventoryOrderRow(
  rx: Prescription,
  isOrderedDrugFromCard: boolean,
): boolean {
  return isOrderedDrugFromCard || isInventoryOrder(rx)
}

const getFulfillmentText = (prescriptionType: PrescriptionType): string => {
  switch (prescriptionType) {
    case PrescriptionType.CHEWY:
      return i18n.t('Common:CHEWY_COM')
    case PrescriptionType.IN_HOUSE:
      return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.IN_HOUSE')
    case PrescriptionType.SCRIPT_OUT:
      return i18n.t('Common:OUTSIDE_PHARMACY')
    default:
      return ''
  }
}

const getLastFill = (rx: Prescription, isInHouse = false): string => {
  const lastFillDate = PrescriptionFields.lastFillDate(rx, isInHouse)
  return DateFormatters.date(lastFillDate)
}

const buildFields = (
  conditionalFields: Array<DisplayField | null | false>,
): DisplayField[] => conditionalFields.filter(Boolean) as DisplayField[]

export const getOrderDate = (rx: Prescription): NullableString =>
  rx.creationDate ?? (rx as { createdAt?: string }).createdAt ?? null

const getCustomViewFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts: GetRxFieldsOptions,
): DisplayField[] => {
  const {
    prescriptionProductSizeUnit = [],
    productSizeUnit = [],
    vetDietConstants,
  } = opts
  const hasUnits =
    prescriptionProductSizeUnit.length > 0 || productSizeUnit.length > 0

  const orderDate = getOrderDate(rx)
  const isOutsidePharmacy = prescriptionType === PrescriptionType.SCRIPT_OUT

  const qtyRequestedField: DisplayField | null = (() => {
    if (!hasUnits) {
      return null
    }
    if (isOutsidePharmacy) {
      const qtyDisplay = getQtyRequested(rx, {
        includeRefills: false,
        prescriptionProductSizeUnit,
        productSizeUnit,
        quantityAndUnitOnly: true,
        vetDietConstants,
      })
      return {
        label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
        value: qtyDisplay.primary,
        secondaryText: qtyDisplay.secondary,
      }
    }
    return {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
      value: createElement(QuantityAndRefills, {
        prescription: rx,
        isVetDiet: getRxType(rx).isVetDiet,
        refillSeparator: ', ',
      }),
    }
  })()

  return buildFields([
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
      value: formatDateOrPlaceholder(orderDate, DateFormatters.dateTime),
    },

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    qtyRequestedField,
  ])
}

const getExpirationField = (rx: Prescription): DisplayField | null => {
  const expirationDate = PrescriptionFields.refillExpirationDate(rx)
  return {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.EXPIRATION'),
    value: formatDateValueOrPlaceholder(expirationDate, DateFormatters.date),
  }
}

const getLastFillField = (
  rx: Prescription,
  prescriptionType: PrescriptionType,
): DisplayField | null => {
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const isChewyWithUsages =
    prescriptionType === PrescriptionType.CHEWY &&
    hasRxUsages(rx as HasRxUsagesValue)
  const lastFill = isChewyWithUsages
    ? (() => {
        const raw = getChewyLastFillDate(rx as RxWithUsages)
        return raw ? DateFormatters.date(raw) : ''
      })()
    : getLastFill(rx, isInHouse)

  const lastRefill =
    lastFill ||
    (isInHouse
      ? EMPTY_FIELD_PLACEHOLDER
      : i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.IN_PROGRESS'))
  return {
    boldValue: !lastFill,
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.LAST_FILL'),
    value: lastRefill,
  }
}

const getStandardFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts?: GetRxFieldsOptions,
): DisplayField[] => {
  const resolvedType = prescriptionType ?? PrescriptionType.IN_HOUSE
  const isScriptOut = prescriptionType === PrescriptionType.SCRIPT_OUT
  const rxForRemainingAndLastFill =
    resolvedType === PrescriptionType.CHEWY &&
    opts?.prescriptionForRefillsRemainingAndLastFill &&
    hasRxUsages(
      opts.prescriptionForRefillsRemainingAndLastFill as HasRxUsagesValue,
    )
      ? (opts.prescriptionForRefillsRemainingAndLastFill as RxWithUsages)
      : rx

  const hideLastFillForChewyPharmacistDeclined =
    resolvedType === PrescriptionType.CHEWY &&
    hasRxUsages(rxForRemainingAndLastFill as HasRxUsagesValue) &&
    (rxForRemainingAndLastFill as RxWithUsages).rxUsages?.some((usage) => {
      if (usage == null || typeof usage !== 'object') {
        return false
      }

      return isChewyPharmacistDeclined({
        orderDisplayStatus: null,
        rxUsageState: usage.rxUsageState,
      })
    }) === true

  return buildFields([
    getExpirationField(rx),

    {
      label: i18n.t('Common:FULFILLMENT'),
      value: getFulfillmentText(resolvedType),
    },

    {
      label: i18n.t(
        'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
      ),
      value: toStringOrZero(PrescriptionFields.numberOfRefills(rx)),
    },

    !isScriptOut &&
      (() => {
        const isChewyWithUsages =
          resolvedType === PrescriptionType.CHEWY &&
          hasRxUsages(rxForRemainingAndLastFill as HasRxUsagesValue)
        const remaining = isChewyWithUsages
          ? getChewyRefillsRemaining({
              ...(rxForRemainingAndLastFill as RxWithUsages),
              quantityNumbRefills:
                (rx as { quantityNumbRefills?: number | null })
                  .quantityNumbRefills ??
                (rxForRemainingAndLastFill as RxWithUsages)
                  .quantityNumbRefills ??
                0,
            })
          : PrescriptionFields.refillsRemaining(rx)
        return {
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.REFILLS_REMAINING'),
          value: toStringOrZero(remaining),
          onClick:
            opts?.onDispenseRefill && remaining > 0
              ? opts.onDispenseRefill
              : undefined,
          tooltipText:
            opts?.onDispenseRefill && remaining > 0
              ? i18n.t('Common:DISPENSE_REFILL')
              : undefined,
        }
      })(),

    isScriptOut || hideLastFillForChewyPharmacistDeclined
      ? null
      : getLastFillField(rxForRemainingAndLastFill, resolvedType),
  ])
}

export const getRxFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetRxFieldsOptions = {},
): DisplayField[] => {
  if (options.isOrderedDrug) {
    return buildFields([
      {
        label: i18n.t('Common:FULFILLMENT'),
        value: getFulfillmentText(PrescriptionType.IN_HOUSE),
      },
      getLastFillField(rx, PrescriptionType.IN_HOUSE),
    ])
  }

  const { prescriptionStates } = options

  const flags = getPrescriptionFlags(rx, prescriptionType, prescriptionStates)
  const suppressStandardFields = shouldSuppressFields(flags)

  return suppressStandardFields
    ? getCustomViewFields(rx, prescriptionType, options)
    : getStandardFields(rx, prescriptionType, options)
}

export const getInstructionsField = (rx: Prescription): DisplayField => ({
  label: i18n.t('Common:INSTRUCTION_PRINTED_ON_LABEL'),
  value: formatValueOrPlaceholder(rx.notes ?? ''),
})

export const getPrescriptionStateName = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): string | null => getPrescriptionFlags(rx, null, prescriptionStates).stateName

/**
 * Checks if a prescription is a vet diet prescription.
 */
export const isVetDietPrescription = (prescription: Prescription): boolean =>
  prescription.rxType === RxType.VetDiet

/**
 * Legacy prescription timeline card: “dispensed from inventory” footer / styling.
 * True for **inventory orders** OR **in-house prescribe** with `origin !== Vet`.
 *
 * **Do not** use this alone for Rx tracker ordered-drug copy — that also matches in-house prescribe
 * refills. Use {@link isOrderedDrugOrInventoryOrderRow} with the card’s `isOrderedDrug` flag there.
 */
export const isDispensedFromInventory = (prescription: {
  origin?: string | null | RxPrescriptionOrigin
  prescriptionType?: string | PrescriptionType
  type?: string
}): boolean => {
  const prescriptionTypeValue = getPrescriptionType(
    prescription.prescriptionType as PrescriptionType | undefined,
  )
  return (
    prescription.type === OrderType.INVENTORY ||
    (prescriptionTypeValue.isInHouse &&
      prescription.origin !== RxPrescriptionOrigin.Vet)
  )
}

/**
 * Checks if a field label matches the QTY_REQUESTED label.
 */
export const isQtyRequestedField = (
  fieldLabel: string,
  qtyRequestedLabel: string,
): boolean => fieldLabel === qtyRequestedLabel

export const getOrderFillStatusText = (
  rx: Prescription,
  prescriptionType?: PrescriptionType,
  isOrderedDrug?: boolean,
): string => {
  const fillDate = getPrescriptionMostRecentDate(rx)
  const effectiveType = prescriptionType ?? rx.prescriptionType

  if (
    isOrderedDrugOrInventoryOrderRow(rx, isOrderedDrug === true) &&
    isDispensedFromInventory({
      ...rx,
      prescriptionType: effectiveType,
    })
  ) {
    return i18n.t(
      'Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_ADMINISTERED',
      { date: DateFormatters.date(fillDate) },
    )
  }

  return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_COMPLETED', {
    date: DateFormatters.date(fillDate),
  })
}

const formatOrderTableQuantity = (
  rx: Prescription,
  parentPrescription: Prescription | undefined,
  opts: OrderTableFieldRowOptions,
  options: { isChewy: boolean; isOrderedDrug?: boolean },
): string => {
  const qtyResult = getQtyRequested(rx, {
    includeRefills: false,
    parentRx: parentPrescription,
    preferAdministrationUnit: options.isOrderedDrug ?? false,
    prescriptionProductSizeUnit: opts.prescriptionProductSizeUnit,
    productSizeUnit: opts.productSizeUnit,
    quantityAndUnitOnly: !options.isChewy,
    usePerRefillQuantity: true,
    vetDietConstants: opts.vetDietConstants,
  })
  return qtyResult.primary
}

export const getOrderTableRow = (
  rx: Prescription,
  options: GetOrderTableRowOptions,
): OrderTableRow[] => {
  const {
    fillIndex,
    isOrderedDrug,
    parentForQty,
    parentPrescription,
    prescriptionType,
    qtyRx,
    timezone,
    totalFills,
    ...opts
  } = options
  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const orderDate = getOrderDate(rx)

  const orderDateCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
    value: formatDateValueOrPlaceholder(orderDate, DateFormatters.dateTime),
    prescription: rx,
    parentPrescription,
    relativeTime: computeRelativeTime(orderDate, timezone),
  }
  const qtyRequestedCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
    value: formatValueOrPlaceholder(
      formatOrderTableQuantity(
        qtyRx ?? rx,
        parentForQty ?? parentPrescription,
        opts,
        { isChewy, isOrderedDrug },
      ),
    ),
    prescription: rx,
    parentPrescription,
  }
  const fillNumberCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_NUMBER'),
    value:
      isChewy && fillIndex === 0
        ? EMPTY_FIELD_PLACEHOLDER
        : `${fillIndex} / ${totalFills}`,
  }

  if (isChewy) {
    return [orderDateCell, qtyRequestedCell, fillNumberCell]
  }

  const fillStatus = getOrderFillStatusText(
    rx,
    prescriptionType ?? undefined,
    isOrderedDrug,
  )
  const fillStatusCell: OrderTableRow = {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS'),
    value: formatValueOrPlaceholder(fillStatus),
  }

  if (isOrderedDrug) {
    return [orderDateCell, qtyRequestedCell, fillNumberCell, fillStatusCell]
  }

  const expirationDate = PrescriptionFields.drugExpirationDate(rx)

  return [
    orderDateCell,
    qtyRequestedCell,
    fillNumberCell,
    {
      label: i18n.t('Common:DRUG_EXPIRATION'),
      value: formatDateValueOrPlaceholder(expirationDate, DateFormatters.date),
    },
    fillStatusCell,
  ]
}

const getCardHeaderForPrescription = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  isOrderedDrug?: boolean,
): string | null => {
  if (prescriptionType === PrescriptionType.CHEWY) {
    return i18n.t('Common:CHEWY_COM')
  }

  const isOrderedDrugVal = isOrderedDrug ?? isInventoryOrder(rx)
  if (isOrderedDrugVal) {
    return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ADMINISTERED_IN_HOUSE')
  }

  if (prescriptionType === PrescriptionType.IN_HOUSE) {
    return rx.origin === RxPrescriptionOrigin.Vet
      ? i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ADMINISTERED_IN_HOUSE')
      : i18n.t('Common:DISPENSED_FROM_INVENTORY')
  }

  return null
}

export const getOrderTableSubHeader = getCardHeaderForPrescription

/**
 * True when the card header should show Order number and P-# (prescription/recipe number).
 * Only for Chewy prescriptions.
 */
export const shouldShowOrderAndPrescriptionNumbers = (
  prescriptionType: NullablePrescriptionType,
): boolean => prescriptionType === PrescriptionType.CHEWY

/**
 * Labels for Chewy order header: "Order number" and "P-#" (or "Recipe number" for vet diet).
 */
export const getChewyOrderInfoLabels = (prescription: Prescription) => {
  const { isVetDiet } = getRxType(prescription)
  return {
    orderNumber: i18n.t('Common:ORDER_NUMBER'),
    prescriptionNumber: isVetDiet
      ? i18n.t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_RECIPE_NUMBER')
      : i18n.t('Dialogs:PRESCRIPTION_DIALOG.CHEWY_PRESCRIPTION_NUMBER'),
  }
}

/** Returns the prescription from a row, optionally filtered by refill status. */
export const getPrescriptionFromRow = <
  T extends { parentPrescription?: Prescription; prescription?: Prescription },
>(
  row: T[],
  isRefill?: boolean,
): Prescription | undefined => {
  const prescription = row.find((cell) => cell.prescription)?.prescription

  if (isRefill === undefined) return prescription

  const hasParent = row.some((cell) => cell.parentPrescription)
  const shouldReturnPrescription = hasParent === isRefill

  return shouldReturnPrescription ? prescription : undefined
}

/** CVC-35572: Shipping shows '--' when row has no order number and no prescription number. */
export const appendShippingColumn = (
  content: OrderTableContent,
  shippingTexts: Array<string | string[]>,
  shippingHeader: string,
): OrderTableContent => {
  const preparingLabel = i18n.t(
    'Clients:TIMELINE.RX_STATUS_TRACKER.PREPARING_FOR_SHIPMENT',
  )
  const isEmpty = (v?: string | null) =>
    !v?.trim() || v === EMPTY_FIELD_PLACEHOLDER

  const headers = [...content.headers, shippingHeader]
  const rows = content.rows.map((row, index) => {
    const header = content.cardHeaders[index]
    const hasOrderOrRx =
      Boolean(header) && (!isEmpty(header.orderNumber) || !isEmpty(header.rxNo))
    const raw = hasOrderOrRx
      ? (shippingTexts[index] ?? preparingLabel)
      : EMPTY_FIELD_PLACEHOLDER
    const value = Array.isArray(raw)
      ? raw.filter(Boolean).join('\n')
      : String(raw)

    return [
      ...row,
      {
        label: shippingHeader,
        value,
      },
    ]
  })
  return { ...content, headers, rows }
}

export function hasOrderTable(
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
): boolean {
  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE
  const isOrderedDrug = (rx as { type?: string }).type === OrderType.INVENTORY
  return isChewy || isInHouse || isOrderedDrug
}

export const getPrescriptionTitle = (
  prescription: Prescription,
  item?: RxCardItem,
  includeType?: boolean,
): string => {
  const baseTitle =
    item?.prescription?.name ??
    item?.prescription?.masterName ??
    LanguageUtils.getTranslatedFieldName(prescription)

  if (includeType) {
    const prescriptionTypeLabel = getPrescriptionTypeLabel({
      prescriptionType: prescription.prescriptionType,
      origin: prescription.origin,
    })
    return `${prescriptionTypeLabel}–${baseTitle}`
  }

  return baseTitle
}

/**
 * Gets the prescription timeline item with origin data merged into prescription.
 */
export const getPrescriptionWithOrigin = (
  item: PrescriptionTimelineItem,
): PrescriptionTimelineItem => ({
  ...item,
  prescription: {
    ...item.prescription,
    origin: item.origin,
  },
})

/**
 * Prepares a prescription for the refill dialog by omitting specified fields.
 * Prevents duplicate charges and incorrect data from being copied to refills.
 *
 * @param prescription - The prescription to prepare
 * @param fieldsToOmit - Prescription keys to omit (e.g., 'id', 'soapId'). 'retailOrderLineItem' is always omitted automatically.
 * @returns An object with cleaned prescription and parent for the refill dialog
 */
export const prepareRefillPrescription = (
  prescription: Prescription,
  fieldsToOmit: (keyof Prescription)[],
): { parent: Prescription; prescription: Prescription } => {
  // Always omit retailOrderLineItem to prevent duplicate charges
  const fieldsToOmitSet = new Set([...fieldsToOmit, 'retailOrderLineItem'])
  const prescriptionFieldsToOmit = Array.from(fieldsToOmitSet)
  // Parent doesn't need 'id' omitted
  const parentFieldsToOmit = prescriptionFieldsToOmit.filter((f) => f !== 'id')

  return {
    prescription: R.omit(
      prescriptionFieldsToOmit,
      prescription,
    ) as Prescription,
    parent: R.omit(parentFieldsToOmit, prescription) as Prescription,
  }
}
